import get from 'lodash/get.js'

import {getState, onlyIfAutoForm, useSelector} from '../../../../store.js'
import {NEW_ID} from '../../../../common/constants/index.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'
import className from '../../../../common/className.js'
import {
  needsWarehouseSelector,
  defaultWarehouseSelector,
} from '../../../../data/warehouses.js'
import {
  autoFillPostageConfigSelector,
  getShippers,
} from '../../../../data/shippers.js'
import {showGlobalError} from '../../../GlobalErrorMessage.js'
import apiverson from '../../../../common/apiverson.js'
import {showWarehouseModal} from '../../../Modals/WarehouseModal.js'
import {showAutoFillPostageModal} from '../../../Modals/AutoFillPostageModal.js'
import PostageBalance from './PostageBalance.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import {showExportUSPSTransactionsModal} from '../ExportUSPSTransactionsModal.js'
import {
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
  simpleSaveShipper,
} from '../ShippersFunctions.js'
import {PITNEY} from '../../../../common/constants/ShipperNames.js'
import Icon from '../../../../common/icons/Icon.js'

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  return baseShipperFormErrorsSelector(state, {formName})
}

export function initialFormSelector(state, {shipper}) {
  return baseShipperInitialFormSelector(state, {
    shipper,
    vendor: PITNEY,
  })
}

export async function saveShipperPitney(formName) {
  return simpleSaveShipper(formName)
}

export async function activatePitneyShipper() {
  try {
    await apiverson.post('/carrier/pitney')

    await getShippers()
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error activating USPS account.',
        details: err.message || err.error_message,
      },
      err,
    )
  }
}

function PitneyForm({formName, form}) {
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))
  const {isEnabled} = useSelector((state) =>
    autoFillPostageConfigSelector(state, {shipperID: form.id}),
  )
  const needsWarehouse = useSelector(needsWarehouseSelector)
  const firstWarehouseID = get(useSelector(defaultWarehouseSelector), 'id')

  if (form.id !== NEW_ID) {
    return (
      <div className="settings-list-item-form-wrap settings-list-item-form-usps clear-both">
        <fieldset>
          <ul className="form-list medium-5 columns margin-bottom-0">
            <Nickname formName={formName} />
            <ShippingMethods formName={formName} />
            <li className="list__item list__item--no-style divider--bottom padding-bottom-20">
              <ButtonPrimary
                className="btn--primary-alt"
                isLoading={form.isSaving}
                isDisabled={errors.preventSave}
                onClick={() => saveShipperPitney(formName)}
              >
                Update
              </ButtonPrimary>
            </li>
          </ul>
        </fieldset>
        <fieldset className="margin-bottom-0">
          <div className="medium-5 columns">
            <PostageBalance shipperID={form.id} />
            <div
              className={className`alert ${
                isEnabled ? 'alert--success' : 'alert--neutral'
              } inline-block`}
            >
              {isEnabled ? (
                <strong className="fs-n0 lh-md v-align-middle margin-right-10">
                  Postage auto-fill is enabled
                </strong>
              ) : (
                <span className="fs-n0 lh-md v-align-middle margin-right-10">
                  Postage auto-fill is disabled
                </span>
              )}
              <ButtonPrimary
                isOutlined
                size="xx-sm"
                onClick={() => showAutoFillPostageModal(form.id)}
              >
                Manage
              </ButtonPrimary>
            </div>
            <dl className="list divider--top margin-top-20">
              <dt className="list__title fs-00">Transaction History</dt>
              <dd className="list__item fs-00">
                <button
                  type="button"
                  className="btn btn--link"
                  onClick={() => showExportUSPSTransactionsModal()}
                >
                  Export CSV
                </button>
              </dd>
            </dl>
          </div>
        </fieldset>
      </div>
    )
  }

  return (
    <div className="medium-7 columns">
      <h4 className="margin-top-20 margin-bottom-5">
        Activate your free USPS account
      </h4>
      <p className="margin-bottom-15">
        {
          'To gain access to our USPS shipping rates and to print USPS labels, you need to activate your '
        }
        <a
          href="//support.ordoro.com/how-do-i-get-a-free-postage-account-with-ordoro/"
          title="Learn more about Ordoro's free USPS accounts"
          target="_blank"
          rel="noopener noreferrer"
        >
          free USPS account
        </a>
        {'.'}
      </p>
      {needsWarehouse ? (
        <div className="alert alert--warning-lt alert--lg align-center margin-bottom-20">
          <Icon
            name="exclamation-triangle"
            className="fs-03 op-30 lh-sm margin-bottom-5"
          />
          <p className="fs-01 margin-bottom-10">
            <strong>
              You need to set a ship-from/warehouse address before you can
              activate your free USPS account.
            </strong>
          </p>
          <ButtonPrimary
            alt
            size="md"
            onClick={() =>
              showWarehouseModal({
                warehouseID: firstWarehouseID,
                openedFrom: 'setup',
              })
            }
          >
            Set a Ship-From Address
          </ButtonPrimary>
        </div>
      ) : (
        <div>
          <ButtonPrimary
            onClick={() => {
              activatePitneyShipper()

              goToShipperSettings()
            }}
          >
            Activate now
          </ButtonPrimary>
          <ButtonSecondary onClick={() => goToShipperSettings()}>
            Cancel
          </ButtonSecondary>
        </div>
      )}
    </div>
  )
}

export default onlyIfAutoForm(PitneyForm, setUpShipperForm)
